import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getErrorMessage } from '@/services/api';
import { insurersService, type BrokerInsurerRow } from '@/services/insurers';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { InsurerLogo } from '@/components/common/InsurerLogo';
import { useAuthStore } from '@/store/auth';
import type { BrokerContext } from '@/types';

type RelationStatus =
  | 'NOT_CONNECTED'
  | 'ACTIVE'
  | 'DISABLED_BY_INSURER'
  | 'DISABLED_BY_BROKER'
  | 'SUSPENDED';

function getStatusBadgeClasses(status: string): string {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200';
    case 'DISABLED_BY_INSURER':
      return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200';
    case 'DISABLED_BY_BROKER':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200';
    case 'SUSPENDED':
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200';
    case 'NOT_CONNECTED':
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100';
  }
}

function formatStatusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function BrokerInsurersPage() {
  const [insurers, setInsurers] = useState<BrokerInsurerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [search, setSearch] = useState<string>('');

  // Whichever broker the BrokerSwitcher last selected. `user.tenant_id` pins
  // exactly one broker and switching does not change it, so without this a
  // multi-broker user reads the wrong broker's connections and counts.
  const user = useAuthStore((s) => s.user);
  const loginType = useAuthStore((s) => s.loginType);
  const tenantContext = useAuthStore((s) => s.tenantContext);
  const brokerId =
    (loginType === 'broker' ? (tenantContext as BrokerContext | null)?.id : undefined) ??
    user?.tenant_id ??
    undefined;

  const fetchInsurers = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await insurersService.brokerInsurerManagement(brokerId);
      setInsurers(rows);
      setLoadError(null);
    } catch (error) {
      // A failed request must not render as "you have no insurers": the empty
      // state below is a factual claim, and this is the one place that can
      // tell the difference.
      const message = getErrorMessage(error);
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [brokerId]);

  useEffect(() => {
    fetchInsurers();
  }, [fetchInsurers]);

  const handleToggleInsurer = async (insurerId: string, enable: boolean) => {
    setToggling(insurerId);
    try {
      await insurersService.toggleBrokerInsurer(insurerId, enable, brokerId);
      toast.success(enable ? 'Insurer connection enabled' : 'Insurer connection disabled');
      fetchInsurers();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setToggling(null);
    }
  };

  /** Connect is now a REQUEST, not a connection.
   *
   *  This button used to POST `toggle_insurer`, which did a bare get_or_create
   *  with status ACTIVE — and an ACTIVE relation is the entitlement, so any
   *  broker could grant itself any insurer's whole catalogue with nobody
   *  approving anything. It files an ask a country admin verifies instead, and
   *  the row goes to "Requested" until they do. */
  const handleRequestConnection = async (insurerId: string) => {
    setToggling(insurerId);
    try {
      const row = await insurersService.requestInsurerConnection(insurerId, undefined, brokerId);
      toast.success(
        row.status === 'pending'
          ? 'Request sent — your country admin will verify it.'
          : `Request ${row.status}.`,
      );
      fetchInsurers();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setToggling(null);
    }
  };

  const renderActionButton = (insurer: BrokerInsurerRow) => {
    const status = insurer.relation_status as RelationStatus;
    const requestStatus = insurer.connection_request_status ?? '';

    switch (status) {
      case 'NOT_CONNECTED':
        if (requestStatus === 'pending') {
          return (
            <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
              Requested — awaiting approval
            </span>
          );
        }
        return (
          <div className="inline-flex flex-col items-end gap-1">
            <button
              onClick={() => handleRequestConnection(insurer.id)}
              disabled={toggling === insurer.id}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {toggling === insurer.id ? 'Requesting...' : 'Request connection'}
            </button>
            {requestStatus === 'declined' && (
              <span className="text-[11px] text-red-500 dark:text-red-400">Previously declined</span>
            )}
          </div>
        );
      case 'ACTIVE':
        return (
          <button
            onClick={() => handleToggleInsurer(insurer.id, false)}
            disabled={toggling === insurer.id}
            className="inline-flex items-center px-3 py-1.5 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 bg-white dark:bg-gray-800 rounded-lg text-xs font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
          >
            {toggling === insurer.id ? 'Disabling...' : 'Disable'}
          </button>
        );
      case 'DISABLED_BY_BROKER':
        return (
          <button
            onClick={() => handleToggleInsurer(insurer.id, true)}
            disabled={toggling === insurer.id}
            className="inline-flex items-center px-3 py-1.5 border border-green-300 dark:border-green-800 text-green-700 dark:text-green-400 bg-white dark:bg-gray-800 rounded-lg text-xs font-medium hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-50"
          >
            {toggling === insurer.id ? 'Enabling...' : 'Enable'}
          </button>
        );
      case 'DISABLED_BY_INSURER':
        return (
          <span className="text-xs text-red-500 dark:text-red-400 font-medium">Disabled by Insurer</span>
        );
      case 'SUSPENDED':
        // Withdrawn by the country admin. Deliberately no broker-side control:
        // this is the status `unassign_insurers` writes precisely because it is
        // not the broker's to undo.
        return (
          <span className="text-xs text-yellow-600 dark:text-yellow-400 font-medium">Withdrawn by country admin</span>
        );
      default:
        return <span className="text-xs text-gray-400">No action available</span>;
    }
  };

  const filteredInsurers = insurers.filter((i) => {
    // BROKER-6a: client-side name/code search (the list is fully loaded).
    const q = search.trim().toLowerCase();
    if (q) {
      const matchesSearch =
        i.name.toLowerCase().includes(q) || i.code.toLowerCase().includes(q);
      if (!matchesSearch) return false;
    }
    if (statusFilter === 'ALL') return true;
    if (statusFilter === 'DISABLED') {
      return i.relation_status === 'DISABLED_BY_INSURER' || i.relation_status === 'DISABLED_BY_BROKER';
    }
    return i.relation_status === statusFilter;
  });

  const statusFilterOptions = [
    { value: 'ALL', label: 'All Insurers' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'NOT_CONNECTED', label: 'Not Connected' },
    { value: 'DISABLED', label: 'Disabled' },
    { value: 'SUSPENDED', label: 'Suspended' },
  ];

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Insurer Management</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Manage your insurer connections and relationships</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or code..."
            className="w-56 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 dark:bg-gray-700 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <div className="w-48">
            <SearchableSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusFilterOptions}
            />
          </div>
          <button
            onClick={fetchInsurers}
            className="inline-flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards (click to filter) */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        {[
          { key: 'ALL', label: 'Total Insurers', value: insurers.length, color: 'text-gray-900 dark:text-gray-100' },
          {
            key: 'ACTIVE',
            label: 'Active',
            value: insurers.filter((i) => i.relation_status === 'ACTIVE').length,
            color: 'text-green-600 dark:text-green-400',
          },
          {
            key: 'NOT_CONNECTED',
            label: 'Not Connected',
            value: insurers.filter((i) => i.relation_status === 'NOT_CONNECTED').length,
            color: 'text-gray-500 dark:text-gray-400',
          },
          {
            key: 'DISABLED',
            label: 'Disabled',
            value: insurers.filter(
              (i) =>
                i.relation_status === 'DISABLED_BY_INSURER' || i.relation_status === 'DISABLED_BY_BROKER',
            ).length,
            color: 'text-red-500 dark:text-red-400',
          },
        ].map((card) => (
          <button
            key={card.key}
            onClick={() => setStatusFilter(card.key)}
            className={`text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-4 transition-colors ${
              statusFilter === card.key ? 'border-primary-500 ring-1 ring-primary-500' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
            }`}
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
            <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
          </button>
        ))}
      </div>

      {/* Insurers Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {loading ? (
          <TableSkeleton />
        ) : loadError ? (
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
              Could not load your insurers
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">{loadError}</p>
            <button
              onClick={fetchInsurers}
              className="mt-4 inline-flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Try again
            </button>
          </div>
        ) : insurers.length === 0 ? (
          <div className="text-center py-12">
            <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">No active insurers in your country</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Only insurance companies that the country admin has marked active appear here. Ask
              your country admin to add and activate insurers for your country.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Insurer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Your policies
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Products
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider"
                    title="Your business connection with this insurer. It is not the API integration status, which the country admin sees on the Integration status screen."
                  >
                    Connection
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filteredInsurers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      No insurers match this filter.
                    </td>
                  </tr>
                )}
                {filteredInsurers.map((insurer) => (
                  <tr key={insurer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* U-03: the company's own mark, with the generic
                            building glyph kept as the fallback for an insurer
                            that has no logo. */}
                        <InsurerLogo
                          src={insurer.logo_display_url}
                          name={insurer.name}
                          size="md"
                        />
                        {(insurer.relation_status as RelationStatus) === 'ACTIVE' ? (
                          <Link
                            to={`/broker/insurers/${insurer.id}/commissions?tab=policies`}
                            className="text-sm font-medium text-primary-700 dark:text-primary-300 hover:underline"
                          >
                            {insurer.name}
                          </Link>
                        ) : (
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{insurer.name}</p>
                        )}
                      </div>
                    </td>
                    {/* The broker's OWN business with this company. The Products
                        column beside it counts the insurer's catalogue, which is
                        the same for every broker on the platform — the two used
                        to sit together and read as if both were ours. */}
                    <td
                      className="px-6 py-4 whitespace-nowrap"
                      data-testid={`own-policies-${insurer.id}`}
                    >
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {insurer.sold_policy_count ?? 0}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400"> total</span>
                      <span className="mx-1 text-gray-300 dark:text-gray-600">·</span>
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                        {insurer.active_policy_count ?? 0}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400"> active</span>
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap"
                      data-testid={`catalogue-${insurer.id}`}
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {insurer.available_policy_count ?? 0} Available
                      </span>
                      <span className="mx-1 text-gray-300 dark:text-gray-600">·</span>
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {insurer.enabled_policy_count ?? 0} enabled
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClasses(
                          insurer.relation_status
                        )}`}
                      >
                        {formatStatusLabel(insurer.relation_status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        {renderActionButton(insurer)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100 dark:bg-gray-700 rounded-t-lg" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 border-t border-gray-100 dark:border-gray-700 flex items-center px-6 gap-4">
          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-2" />
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-24" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20" />
        </div>
      ))}
    </div>
  );
}
